import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADMIN_DATA_KEY, API_TOKEN_KEY } from '../config/api';
import * as api from '../services/api';
import { unregisterFcmOnLogout } from '../services/fcmPush';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

const loadStored = () => {
  try {
    const token = localStorage.getItem(API_TOKEN_KEY);
    const adminJson = localStorage.getItem(ADMIN_DATA_KEY);
    const admin = adminJson ? JSON.parse(adminJson) : null;
    return { token, admin };
  } catch {
    return { token: null, admin: null };
  }
};

const initialStored = loadStored();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [token, setTokenState] = useState(initialStored.token);
  const [admin, setAdminState] = useState(initialStored.admin);
  const [loading, setLoading] = useState(!!initialStored.token);

  const setAuth = useCallback((newToken, newAdmin) => {
    setTokenState(newToken);
    setAdminState(newAdmin || null);
    if (newToken) {
      localStorage.setItem(API_TOKEN_KEY, newToken);
      if (newAdmin) {
        localStorage.setItem(ADMIN_DATA_KEY, JSON.stringify(newAdmin));
      }
    } else {
      localStorage.removeItem(API_TOKEN_KEY);
      localStorage.removeItem(ADMIN_DATA_KEY);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await unregisterFcmOnLogout();
    } catch (err) {
      console.warn('FCM cleanup on logout failed:', err);
    }
    setAuth(null, null);
    navigate('/login', { replace: true });
  }, [navigate, setAuth]);

  useEffect(() => {
    const { token: storedToken, admin: storedAdmin } = loadStored();
    if (!storedToken) {
      setLoading(false);
      return;
    }
    setTokenState(storedToken);
    setAdminState(storedAdmin);
    api.getMe()
      .then((res) => {
        const adminData = res.data?.admin ?? res.admin ?? storedAdmin;
        if (adminData) {
          setAdminState(adminData);
          localStorage.setItem(ADMIN_DATA_KEY, JSON.stringify(adminData));
        }
        setLoading(false);
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 401) {
          setAuth(null, null);
        }
        setLoading(false);
      });
  }, [setAuth]);

  const value = useMemo(
    () => ({
      token,
      admin,
      isAuthenticated: !!token,
      setAuth,
      logout,
      loading,
    }),
    [token, admin, setAuth, logout, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
